import type { ApiClient } from '../../rest-api/generated/laravel-typed-openapi';

/**
 * How many people the organization's plan holds, and how many places are taken.
 *
 * From `GET /organization/plan`, which every member may read. The backend keeps Clerk's
 * `max_allowed_memberships` equal to `limit` (MDC-4501), so Clerk refuses an invite past it; these
 * numbers only decide what the app offers.
 */
export type OrganizationSeats = {
  /** Members the plan includes, the owner counted. 0 is unlimited, as in Clerk. */
  limit: number;
  members: number;
  /** Invitations not yet answered. Each holds a place. */
  pendingInvitations: number;
};

export const fetchOrganizationSeats = async (apiClient: ApiClient): Promise<OrganizationSeats> => {
  const { data } = await apiClient.get('/organization/plan');

  return {
    limit: data.limits.members,
    members: data.usage.members,
    // `null` when the backend couldn't read Clerk: counted as none, so a slow Clerk never hides the
    // invite. Clerk still refuses one past the limit.
    pendingInvitations: data.usage.pending_invitations ?? 0,
  };
};

/** Whether every place on the plan is taken, by members or by invitations still open. */
export const isOrganizationFull = ({ limit, members, pendingInvitations }: OrganizationSeats): boolean =>
  limit > 0 && members + pendingInvitations >= limit;
